import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'
import { cn } from '@/lib/utils'
import {
    ArrowLeft,
    Save,
    FileDown,
    Loader2,
    Bold,
    Italic,
    Type,
    MessageSquare,
    History,
} from 'lucide-react'

interface Script {
    id: string
    title: string
    author: string | null
    logline: string | null
    version: number
    content: ScriptContent
    status: string
    wordCount: number
    pageCount: number
    updatedAt: number
}

interface ScriptContent {
    elements: ScriptElement[]
    metadata: {
        title: string
        author: string
        copyright?: string
        contact?: string
        date?: string
        draft?: string
    }
}

interface ScriptElement {
    id: string
    type: 'scene_heading' | 'action' | 'character' | 'dialogue' | 'parenthetical' | 'transition' | 'shot' | 'note'
    content: string
    metadata?: Record<string, unknown>
}

const ELEMENT_TYPES = [
    { type: 'scene_heading', label: 'Scene Heading', shortcut: 'INT./EXT.' },
    { type: 'action', label: 'Action', shortcut: '' },
    { type: 'character', label: 'Character', shortcut: '' },
    { type: 'dialogue', label: 'Dialogue', shortcut: '' },
    { type: 'parenthetical', label: 'Parenthetical', shortcut: '()' },
    { type: 'transition', label: 'Transition', shortcut: 'CUT TO:' },
]

export default function ScriptEditorPage() {
    const { projectId, scriptId } = useParams<{ projectId: string; scriptId: string }>()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const [title, setTitle] = useState('')
    const [elements, setElements] = useState<ScriptElement[]>([])
    const [selectedElement, setSelectedElement] = useState<string | null>(null)
    const [currentType, setCurrentType] = useState<ScriptElement['type']>('action')
    const [hasChanges, setHasChanges] = useState(false)
    const [collaborators] = useState<Array<{ id: string; name: string; color: string }>>([])

    const { data: scriptData, isLoading } = useQuery({
        queryKey: ['script', scriptId],
        queryFn: () => api.get<Script>(`/projects/${projectId}/scripts/${scriptId}`),
        enabled: !!scriptId,
    })

    const saveMutation = useMutation({
        mutationFn: (data: { title: string; content: string }) =>
            api.patch(`/projects/${projectId}/scripts/${scriptId}`, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['script', scriptId] })
            setHasChanges(false)
            toast({ title: 'Saved', description: 'Script saved successfully' })
        },
        onError: (error: any) => {
            toast({ title: 'Save failed', description: error.message, variant: 'destructive' })
        },
    })

    useEffect(() => {
        if (scriptData?.data) {
            setTitle(scriptData.data.title)
            setElements(scriptData.data.content?.elements || [])
        }
    }, [scriptData])

    const handleSave = useCallback(() => {
        const content: ScriptContent = {
            elements,
            metadata: {
                title,
                author: scriptData?.data?.author || '',
            },
        }
        saveMutation.mutate({ title, content: JSON.stringify(content) })
    }, [title, elements, scriptData, saveMutation])

    // Auto-save on Ctrl/Cmd+S
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault()
                handleSave()
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [handleSave])

    const addElement = (type: ScriptElement['type']) => {
        const newElement: ScriptElement = {
            id: `el-${Date.now()}`,
            type,
            content: '',
        }
        setElements([...elements, newElement])
        setSelectedElement(newElement.id)
        setHasChanges(true)
    }

    const updateElement = (id: string, content: string) => {
        setElements(elements.map(el =>
            el.id === id ? { ...el, content } : el
        ))
        setHasChanges(true)
    }

    const handleKeyDown = (e: React.KeyboardEvent, elementId: string, index: number) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            // Determine next element type based on current
            const currentEl = elements.find(el => el.id === elementId)
            let nextType: ScriptElement['type'] = 'action'

            if (currentEl?.type === 'character') nextType = 'dialogue'
            else if (currentEl?.type === 'dialogue') nextType = 'action'
            else if (currentEl?.type === 'scene_heading') nextType = 'action'

            const newElement: ScriptElement = {
                id: `el-${Date.now()}`,
                type: nextType,
                content: '',
            }

            const newElements = [...elements]
            newElements.splice(index + 1, 0, newElement)
            setElements(newElements)
            setSelectedElement(newElement.id)
            setCurrentType(nextType)
            setHasChanges(true)
        } else if (e.key === 'Backspace' && elements[index]?.content === '' && elements.length > 1) {
            e.preventDefault()
            const newElements = elements.filter((_, i) => i !== index)
            setElements(newElements)
            setSelectedElement(newElements[Math.max(0, index - 1)]?.id || null)
            setHasChanges(true)
        }
    }

    const getElementClass = (type: ScriptElement['type']) => {
        const classes: Record<string, string> = {
            scene_heading: 'uppercase font-bold',
            action: '',
            character: 'uppercase text-center ml-[2.5in]',
            dialogue: 'mx-[1.5in]',
            parenthetical: 'italic mx-[2in]',
            transition: 'uppercase text-right',
            shot: 'uppercase',
            note: 'italic text-muted-foreground bg-yellow-500/10 p-2 rounded',
        }
        return classes[type] || ''
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        )
    }

    const script = scriptData?.data

    if (!script) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px]">
                <p className="text-muted-foreground mb-4">Script not found</p>
                <Button onClick={() => navigate(`/projects/${projectId}`)}>Back to Project</Button>
            </div>
        )
    }

    return (
        <div className="flex flex-col h-[calc(100vh-8rem)]">
            {/* Toolbar */}
            <div className="flex items-center justify-between pb-4 border-b">
                <div className="flex items-center gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate(`/projects/${projectId}`)}>
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <Input
                        value={title}
                        onChange={(e) => { setTitle(e.target.value); setHasChanges(true) }}
                        className="text-lg font-semibold border-none bg-transparent w-64 focus-visible:ring-0"
                    />
                    {hasChanges && (
                        <span className="text-xs text-muted-foreground">• Unsaved changes</span>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {/* Collaborators */}
                    {collaborators.length > 0 && (
                        <div className="flex -space-x-2 mr-2">
                            {collaborators.map((c) => (
                                <div
                                    key={c.id}
                                    className="w-7 h-7 rounded-full border-2 border-background flex items-center justify-center text-xs text-white"
                                    style={{ backgroundColor: c.color }}
                                    title={c.name}
                                >
                                    {c.name.charAt(0)}
                                </div>
                            ))}
                        </div>
                    )}

                    <Button variant="outline" size="sm">
                        <History className="mr-2 h-4 w-4" />
                        v{script.version}
                    </Button>

                    <Button variant="outline" size="sm">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Comments
                    </Button>

                    <Button variant="outline" size="sm">
                        <FileDown className="mr-2 h-4 w-4" />
                        Export
                    </Button>

                    <Button size="sm" onClick={handleSave} disabled={saveMutation.isPending || !hasChanges}>
                        {saveMutation.isPending ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <Save className="mr-2 h-4 w-4" />
                        )}
                        Save
                    </Button>
                </div>
            </div>

            {/* Format toolbar */}
            <div className="flex items-center gap-1 py-2 border-b">
                {ELEMENT_TYPES.map(({ type, label }) => (
                    <Button
                        key={type}
                        variant={currentType === type ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => {
                            setCurrentType(type as ScriptElement['type'])
                            if (selectedElement) {
                                setElements(elements.map(el =>
                                    el.id === selectedElement ? { ...el, type: type as ScriptElement['type'] } : el
                                ))
                                setHasChanges(true)
                            }
                        }}
                    >
                        {label}
                    </Button>
                ))}
                <div className="h-6 w-px bg-border mx-2" />
                <Button variant="ghost" size="icon">
                    <Bold className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon">
                    <Italic className="h-4 w-4" />
                </Button>
            </div>

            {/* Editor */}
            <div className="flex-1 overflow-auto py-8">
                <div className="screenplay-editor mx-auto bg-white dark:bg-white text-black rounded shadow-lg min-h-full">
                    {/* Title page header */}
                    <div className="text-center mb-16">
                        <h1 className="text-xl font-bold uppercase">{title}</h1>
                        {script.author && (
                            <p className="mt-4">by</p>
                        )}
                        {script.author && (
                            <p className="mt-1">{script.author}</p>
                        )}
                    </div>

                    {/* Script content */}
                    <div className="space-y-2">
                        {elements.length === 0 ? (
                            <div
                                className="text-center text-gray-400 py-8 cursor-pointer hover:bg-gray-50 rounded"
                                onClick={() => addElement('scene_heading')}
                            >
                                Click to add your first scene heading...
                            </div>
                        ) : (
                            elements.map((element, index) => (
                                <div
                                    key={element.id}
                                    className={cn(
                                        "screenplay-element relative group",
                                        selectedElement === element.id && "ring-2 ring-primary/50 rounded",
                                        getElementClass(element.type)
                                    )}
                                >
                                    <textarea
                                        value={element.content}
                                        onChange={(e) => updateElement(element.id, e.target.value)}
                                        onFocus={() => {
                                            setSelectedElement(element.id)
                                            setCurrentType(element.type)
                                        }}
                                        onKeyDown={(e) => handleKeyDown(e, element.id, index)}
                                        placeholder={element.type === 'scene_heading' ? 'INT./EXT. LOCATION - TIME' : ''}
                                        className={cn(
                                            "w-full bg-transparent border-none outline-none resize-none font-screenplay text-base leading-normal",
                                            getElementClass(element.type)
                                        )}
                                        rows={1}
                                        style={{
                                            minHeight: '1.5em',
                                            height: 'auto',
                                            fontFamily: "'Courier Prime', 'Courier New', monospace",
                                        }}
                                        onInput={(e) => {
                                            const target = e.target as HTMLTextAreaElement
                                            target.style.height = 'auto'
                                            target.style.height = target.scrollHeight + 'px'
                                        }}
                                    />
                                    <button
                                        className="absolute -left-8 top-0 opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-gray-600"
                                        onClick={() => {
                                            // Show element type selector
                                        }}
                                    >
                                        <Type className="h-4 w-4" />
                                    </button>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Add element button */}
                    {elements.length > 0 && (
                        <div className="mt-8 text-center">
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => addElement(currentType)}
                                className="text-gray-400"
                            >
                                + Add {ELEMENT_TYPES.find(t => t.type === currentType)?.label || 'Element'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer status bar */}
            <div className="flex items-center justify-between py-2 border-t text-xs text-muted-foreground">
                <div className="flex items-center gap-4">
                    <span>Page {Math.max(1, Math.ceil(elements.length / 55))} of ~{Math.max(1, Math.ceil(elements.length / 55))}</span>
                    <span>{elements.reduce((acc, el) => acc + el.content.split(/\s+/).filter(Boolean).length, 0)} words</span>
                </div>
                <div className="flex items-center gap-4">
                    <span className="capitalize">{script.status}</span>
                    <span>Version {script.version}</span>
                </div>
            </div>
        </div>
    )
}
